import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Button, Dialog, Input, Price, PriceInput, Select } from '@artosera/ui';
import {
  useEncaissementPaiement,
  useFacturePaiement,
  useInscrireAlertePaiement,
  useRembourserPaiement,
  useReprendreRemboursementPaiement,
  type CorpsAlertePaiement,
  type CorpsRemboursementPaiement,
  type EncaissementPaiement,
} from '../../api/paiementEnLigne.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ErreurPaiement, TentativesPaiement, useTextePaiement } from './paiementUi.js';
import styles from './PaiementEnLigne.module.css';

export function EncaissementDetailPage() {
  const { id = '' } = useParams();
  const { galerieSlug, galerie } = useShellContext();
  const { t, label } = useTextePaiement();
  const lecture = useEncaissementPaiement(galerieSlug, id);
  return (
    <div data-ui="page" className={styles.page}>
      <h1>{label('encaissement')}</h1>
      <Link to={`/${galerieSlug}/paiement-en-ligne`}>{label('titre')}</Link>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      <ErreurPaiement erreur={lecture.error} />
      {lecture.data && (
        <Detail
          encaissement={lecture.data.encaissement}
          galerieSlug={galerieSlug}
          peutGerer={galerieALaction(galerie, 'online-payments:manage')}
        />
      )}
    </div>
  );
}
function Detail({
  encaissement: e,
  galerieSlug,
  peutGerer,
}: {
  encaissement: EncaissementPaiement;
  galerieSlug: string;
  peutGerer: boolean;
}) {
  const { t, label, langue } = useTextePaiement();
  const facture = useFacturePaiement(galerieSlug, e.factureId);
  const remboursement = useRembourserPaiement(galerieSlug);
  const reprise = useReprendreRemboursementPaiement(galerieSlug);
  const alerte = useInscrireAlertePaiement(galerieSlug);
  const [etape, setEtape] = useState<'saisie' | 'confirmation' | null>(null);
  const [corps, setCorps] = useState<CorpsRemboursementPaiement>({
    part: 'excedent',
    montantCentimes: 0,
    motif: '',
  });
  const [rangAlerte, setRangAlerte] = useState<number | null>(null);
  const [inscription, setInscription] = useState<CorpsAlertePaiement>({ mode: 'avoir', motif: '' });
  const factureUrl = `/${galerieSlug}/ventes/${e.factureId}`;
  const valide = corps.montantCentimes > 0 && !!corps.motif.trim();
  return (
    <>
      <section className={styles.section}>
        <h2>{label(`etats.${e.etat}`)}</h2>
        <Link to={factureUrl}>{label('voirFacture')}</Link>
        <dl className={styles.totaux}>
          {(
            [
              'montantCentimes',
              'imputeCentimes',
              'excedentCentimes',
              'fraisCentimes',
              'rembourseExcedentCentimes',
              'rembourseImputeCentimes',
            ] as const
          ).map((c) => (
            <div key={c}>
              <dt>{label(c)}</dt>
              <dd>
                {e[c] === null ? '—' : <Price cents={e[c]} currency={e.devise} lang={langue} />}
              </dd>
            </div>
          ))}
          <dt>{label('moyen')}</dt>
          <dd>{label(`moyens.${e.moyen}`)}</dd>
          <dt>{label('reglement')}</dt>
          <dd>{e.reglementId ? <Link to={factureUrl}>{e.reglementId}</Link> : '—'}</dd>
          <dt>{label('tentative')}</dt>
          <dd>{e.tentativeId}</dd>
        </dl>
        {peutGerer && (
          <Button
            onClick={() => {
              remboursement.reset();
              setCorps({ part: 'excedent', montantCentimes: 0, motif: '' });
              setEtape('saisie');
            }}
          >
            {label('rembourser')}
          </Button>
        )}
      </section>
      <ErreurPaiement erreur={facture.error} />
      {facture.data && <TentativesPaiement tentatives={facture.data.tentatives} />}
      <section className={styles.section}>
        <h2>{label('remboursements')}</h2>
        <ErreurPaiement erreur={reprise.error} factureUrl={factureUrl} />
        {e.remboursements.length === 0 && <p>{label('aucun')}</p>}
        <ul>
          {e.remboursements.map((r) => (
            <li key={r.cle}>
              {label(`parts.${r.part}`)} ·{' '}
              <Price cents={r.montantCentimes} currency={e.devise} lang={langue} /> ·{' '}
              {label(`etats.${r.etat}`)} · {r.motif}
              {r.avoirId && (
                <>
                  {' '}
                  · <Link to={`/${galerieSlug}/ventes/${r.avoirId}`}>{label('avoir')}</Link>
                </>
              )}
              {peutGerer && r.etat === 'demande' && (
                <Button
                  loading={reprise.isPending}
                  onClick={() => reprise.mutate({ id: e._id, cle: r.cle })}
                >
                  {label('reprendreRemboursement')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className={styles.section}>
        <h2>{label('alertes')}</h2>
        {e.alertes.length === 0 && <p>{label('aucun')}</p>}
        <ul>
          {e.alertes.map((a, rang) => (
            <li key={a.evenementId}>
              {label(`alertesTypes.${a.type}`)} ·{' '}
              <Price cents={a.montantCentimes} currency={e.devise} lang={langue} /> ·{' '}
              {label(a.inscrite ? 'inscrite' : 'aInscrire')}
              {a.mode && <> · {label(a.mode)}</>}
              {a.motif && <> · {a.motif}</>}
              {peutGerer && !a.inscrite && (
                <Button
                  onClick={() => {
                    alerte.reset();
                    setInscription({ mode: 'avoir', motif: '' });
                    setRangAlerte(rang);
                  }}
                >
                  {label('inscrire')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={etape !== null}
        onOpenChange={(v) => {
          if (!remboursement.isPending) setEtape(v ? 'saisie' : null);
        }}
        title={label(etape === 'confirmation' ? 'confirmerRemboursement' : 'rembourser')}
        closeLabel={t('commun:fermer')}
      >
        {etape === 'saisie' && (
          <form
            className={styles.formulaire}
            onSubmit={(event) => {
              event.preventDefault();
              if (valide) setEtape('confirmation');
            }}
          >
            <Field label={label('part')} htmlFor="remboursement-part">
              <Select
                id="remboursement-part"
                value={corps.part}
                onValueChange={(part) =>
                  setCorps({ ...corps, part: part as CorpsRemboursementPaiement['part'] })
                }
                options={['excedent', 'impute'].map((v) => ({
                  value: v,
                  label: label(`parts.${v}`),
                }))}
              />
            </Field>
            <Field label={label('montant')} htmlFor="remboursement-montant">
              <PriceInput
                id="remboursement-montant"
                cents={corps.montantCentimes}
                lang={langue}
                currency={e.devise}
                onCentsChange={(montantCentimes) => setCorps({ ...corps, montantCentimes })}
              />
            </Field>
            <Field label={label('motif')} htmlFor="remboursement-motif">
              <Input
                id="remboursement-motif"
                required
                maxLength={1000}
                value={corps.motif}
                onChange={(event) => setCorps({ ...corps, motif: event.target.value })}
              />
            </Field>
            <Button type="submit" disabled={!valide}>
              {label('continuer')}
            </Button>
          </form>
        )}
        {etape === 'confirmation' && (
          <>
            <p>
              {label('confirmerMontant')}{' '}
              <Price cents={corps.montantCentimes} currency={e.devise} lang={langue} /> ·{' '}
              {label(`parts.${corps.part}`)}
            </p>
            <p>{corps.motif}</p>
            <ErreurPaiement erreur={remboursement.error} factureUrl={factureUrl} />
            <div className={styles.actions}>
              <Button disabled={remboursement.isPending} onClick={() => setEtape('saisie')}>
                {label('retour')}
              </Button>
              <Button
                disabled={!valide || remboursement.isError}
                loading={remboursement.isPending}
                onClick={() => {
                  void remboursement
                    .mutateAsync({ id: e._id, corps: { ...corps, motif: corps.motif.trim() } })
                    .then(() => setEtape(null))
                    .catch(() => {});
                }}
              >
                {label('confirmer')}
              </Button>
            </div>
          </>
        )}
      </Dialog>
      <Dialog
        open={rangAlerte !== null}
        onOpenChange={(v) => {
          if (!v) setRangAlerte(null);
        }}
        title={label('inscrire')}
        closeLabel={t('commun:fermer')}
      >
        <form
          className={styles.formulaire}
          onSubmit={(event) => {
            event.preventDefault();
            if (rangAlerte !== null)
              void alerte
                .mutateAsync({
                  id: e._id,
                  rang: rangAlerte,
                  corps: { ...inscription, motif: inscription.motif.trim() },
                })
                .then(() => setRangAlerte(null))
                .catch(() => {});
          }}
        >
          <Field label={label('mode')} htmlFor="alerte-mode">
            <Select
              id="alerte-mode"
              value={inscription.mode}
              onValueChange={(mode) =>
                setInscription({ ...inscription, mode: mode as CorpsAlertePaiement['mode'] })
              }
              options={['avoir', 'traite'].map((v) => ({ value: v, label: label(v) }))}
            />
          </Field>
          <Field label={label('motif')} htmlFor="alerte-motif">
            <Input
              id="alerte-motif"
              required
              maxLength={1000}
              value={inscription.motif}
              onChange={(event) => setInscription({ ...inscription, motif: event.target.value })}
            />
          </Field>
          <ErreurPaiement erreur={alerte.error} factureUrl={factureUrl} />
          <Button type="submit" disabled={!inscription.motif.trim()} loading={alerte.isPending}>
            {label('confirmer')}
          </Button>
        </form>
      </Dialog>
    </>
  );
}
